// src/services/anchorService.js
// Anchor server service - resolves email addresses to Matrix identities

import logger from '../utils/logger';

// Trusted domains for matrixServerName validation (defense-in-depth)
const TRUSTED_DOMAINS = ['netsfere.com', 'netsferedev.com', 'netsferetest.com'];

/**
 * Validate that a hostname belongs to a trusted domain
 * @param {string} hostname - The hostname to validate
 * @returns {boolean} Whether the hostname is trusted
 */
function isTrustedMatrixServer(hostname) {
  const h = hostname.toLowerCase();
  return TRUSTED_DOMAINS.some(d => h === d || h.endsWith('.' + d));
}

/**
 * Map email domain to anchor server hostname
 * @param {string} emailDomain - The domain part of the email address
 * @returns {string} The anchor server hostname
 */
function getAnchorHost(emailDomain) {
  const domain = emailDomain.toLowerCase();
  if (domain === 'netsferedev.org' || domain.endsWith('.netsferedev.org')) {
    return 'anchor.netsferedev.com';
  }
  if (domain === 'netsferetest.org' || domain.endsWith('.netsferetest.org')) {
    return 'anchor.netsferetest.com';
  }
  return 'anchor.netsfere.com';
}

/**
 * Resolve an email address via the anchor server REST API
 * @param {string} email - The email address to resolve
 * @param {Object} options - Options
 * @param {number} options.timeout - Timeout in milliseconds (default: 15000)
 * @param {AbortSignal} options.signal - External abort signal (e.g. from component unmount)
 * @returns {Promise<{matrixId: string, matrixServerName: string, displayName: string, type: string}>}
 * @throws {Error} On network failure, timeout, HTTP error, or invalid response
 */
async function resolveEmail(email, options = {}) {
  const { timeout = 15000, signal: externalSignal } = options;

  const domain = email.split('@')[1];
  if (!domain) {
    throw new Error('Invalid email address');
  }

  const anchorHost = getAnchorHost(domain);
  const url = `https://${anchorHost}/7/anchor-server/resolve_ti.php?email=${encodeURIComponent(email)}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  // Abort on external signal (component unmount)
  const onAbort = () => controller.abort();
  if (externalSignal) {
    if (externalSignal.aborted) {
      clearTimeout(timeoutId);
      throw new DOMException('Aborted', 'AbortError');
    }
    externalSignal.addEventListener('abort', onAbort);
  }

  try {
    logger.debug('Anchor: resolving email via', url);

    const response = await fetch(url, {
      method: 'GET',
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`Anchor server returned HTTP ${response.status}`);
    }

    const data = await response.json();

    // Missing type → treat as "Not Found" (proceed to password step)
    if (!data.type) {
      return { type: 'Not Found' };
    }

    // "Not Found" — no matrixId/matrixServerName expected
    if (data.type === 'Not Found') {
      return { type: 'Not Found' };
    }

    // SSO type requires idpName
    if (data.type === 'SSO' && !data.idpName) {
      throw new Error('Anchor SSO response missing idpName');
    }

    // Unknown type — return as-is so LoginForm can show forward-compat error
    const KNOWN_TYPES = ['Not Found', 'SSO'];
    if (!KNOWN_TYPES.includes(data.type)) {
      return data;
    }

    // All non-"Not Found" types require matrixServerName
    if (!data.matrixServerName) {
      throw new Error('Anchor response missing matrixServerName');
    }

    // Validate matrixServerName is a safe hostname
    const hostnameRegex = /^[a-zA-Z0-9]([a-zA-Z0-9.-]*[a-zA-Z0-9])?$/;
    if (!hostnameRegex.test(data.matrixServerName)) {
      throw new Error('Invalid matrixServerName format');
    }

    // Validate against trusted domain allowlist (defense-in-depth)
    if (!isTrustedMatrixServer(data.matrixServerName)) {
      throw new Error('matrixServerName not in trusted domains');
    }

    return data;
  } catch (error) {
    // Distinguish internal timeout from external abort
    if (externalSignal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    // Internal timeout — re-throw as a regular Error so LoginForm displays it
    if (error.name === 'AbortError') {
      throw new Error('Anchor server request timed out');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
    externalSignal?.removeEventListener('abort', onAbort);
  }
}

export { getAnchorHost, resolveEmail };
export default { getAnchorHost, resolveEmail };
